// Pestaña "Material" de la ficha del presupuesto (formato nuevo).
//
// Lista del material del evento calculada con la misma consolidación que la
// hoja de material, pero por evento: todo va al mismo sitio, así que el material
// reutilizable entre servicios no se suma (máximo) y los consumibles y
// desechables sí (suma). Las cajas llevan su desglose (solo informativo).
//
// - Unidades editables por fila con "Restablecer". Con `autoguardado` (ficha
//   nueva) se guardan solas por la cola de la ficha (1,5 s tras escribir o al
//   salir del campo); sin él, al salir del campo.
// - "Validar material" congela la lista: la hoja de material sale con ella y la
//   pestaña queda en solo lectura con "Reabrir". Es obligatorio: arriba del todo
//   va el estado (pendiente, validado o cambiado desde la validación) con el
//   botón; sin validar, la hoja de material sale como borrador.
// - Si cambian personas, menús o servicios después de validar, se avisa (sin
//   desvalidar solo).
// - Multievento: una lista por jornada (la hoja de material se saca por jornada).
// - Lote E: si lo validado ya no cuadra con lo calculado ahora
//   (`desactualizada`), aviso con "Revalidar" (la hoja sale con lo actual y la
//   franja "Revalidar"). Menaje de cocina (cafeteras, termos, etiquetas...) y
//   bandejas de alérgicos, solo informativo. Desplegable "Diseño (pruebas)"
//   para descargar la hoja con uno de los cinco diseños en pruebas.
//
// Todo lo calcula el backend (material_evento_routes.py); aquí solo se muestra
// y se guarda.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/api';
import SelectorJornadas from './SelectorJornadas';

const claseBoton = 'h-11 md:h-[34px] px-3 rounded-lg border text-sm md:text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;
const botonSecundario = `${claseBoton} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`;
const claseTh = 'px-3 py-2 text-xs font-bold tracking-wide uppercase text-[#5B6B64] border-b border-[#E1E6E3]';
const claseTd = 'px-3 py-2.5 text-sm border-b border-[#EEF1EF] align-top';

const formatFechaHora = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  const f = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`);
  if (Number.isNaN(f.getTime())) return '';
  return f.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCantidad = (n) => (Number(n) || 0).toLocaleString('es-ES', { maximumFractionDigits: 1 });
const conUnidad = (n, unidad) => `${formatCantidad(n)}${unidad && unidad !== 'ud' ? ` ${unidad}` : ''}`;

const textoRegla = (f) => {
  if (f.es_ingrediente) return 'Se consume: suma de los servicios';
  if (f.regla === 'maximo') {
    return (f.detalle || []).length > 1 ? 'Reutilizable: el máximo entre servicios' : 'Reutilizable';
  }
  return (f.detalle || []).length > 1 ? 'Consumible: suma de los servicios' : 'Consumible';
};

const DISENOS_HOJA = [
  { valor: '', texto: '2 · Por servicio y hora (por defecto)' },
  { valor: '1', texto: '1 · Checklist de carga' },
  { valor: '0', texto: 'Hoja antigua' },
  { valor: '3', texto: '3 · Tarjetas por caja' },
  { valor: '4', texto: '4 · Tabla con semáforo' },
  { valor: '5', texto: '5 · Montaje y recogida' },
];

// `articulosAlquiler`: líneas de alquiler del proveedor (las cuenta la ficha);
// con `onAbrirAlquiler`, al final de la lista sale "Material de alquiler: llega
// al evento" (líneas del proveedor + material de menús "Es alquiler", que el
// backend ya no mete en `filas`) con el enlace "Ver" que abre el panel.
const PestanaMaterial = ({ presupuestoId, presupuesto, onCambio, onEstadoGuardado, onDescargarHoja, autoguardado = null, articulosAlquiler = 0, onAbrirAlquiler = null }) => {
  const [datos, setDatos] = useState(null);
  const [diseno, setDiseno] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [jornadaId, setJornadaId] = useState(null);
  const [borradores, setBorradores] = useState({}); // {nombre: texto} mientras se edita
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);
  const autoguardadoRef = useRef(autoguardado);
  autoguardadoRef.current = autoguardado;
  const borradoresRef = useRef(borradores);
  borradoresRef.current = borradores;
  const datosRef = useRef(datos);
  datosRef.current = datos;

  // Lo que cambia la lista: si cambia, se vuelve a pedir al backend
  const firma = JSON.stringify([
    presupuesto?.num_personas,
    (presupuesto?.menus_personalizados || []).map(m => [m.menu_id, m.num_personas, m.platos_ids, m.hora_inicio_servicio, m.hora_fin_servicio, m.unidades_por_plato]),
    (presupuesto?.jornadas || []).map(j => [j.id, j.num_personas, j.hora_inicio, j.hora_fin, (j.menus_personalizados || []).map(m => [m.menu_id, m.num_personas, m.platos_ids, m.hora_inicio_servicio, m.hora_fin_servicio, m.unidades_por_plato])]),
    presupuesto?.hora_inicio,
    presupuesto?.hora_fin,
    presupuesto?.bebidas_modo,
    (presupuesto?.comensales_alergicos || []).length,
  ]);

  // `conservar`: claves cuyo borrador se mantiene (se siguen editando)
  const aplicar = useCallback((data, conservar = null) => {
    setDatos(data);
    setBorradores(prev => (conservar
      ? Object.fromEntries(Object.entries(prev).filter(([k]) => conservar(k, prev[k])))
      : {}));
    if (data?.jornada_id) setJornadaId(data.jornada_id);
    if (onCambio && data) onCambio(data);
  }, [onCambio]);

  useEffect(() => {
    if (!presupuestoId) return undefined;
    let cancelado = false;
    setCargando(true);
    api.get(`/presupuestos/${presupuestoId}/material-evento`, jornadaId ? { params: { jornada_id: jornadaId } } : undefined)
      .then(({ data }) => {
        if (cancelado) return;
        if (!data || !Array.isArray(data.filas)) {
          setDatos(null);
          setError('No se pudo cargar el material del evento.');
          return;
        }
        setError(null);
        aplicar(data);
      })
      .catch((err) => {
        if (!cancelado) setError(err?.response?.data?.detail || 'No se pudo cargar el material del evento.');
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
    // `aplicar` cambia con onCambio; la carga depende del presupuesto y la jornada
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presupuestoId, jornadaId, firma]);

  const enviarAhora = async (metodo, ruta, cuerpo, textoOk, { automatico = false, clave = null, texto } = {}) => {
    if (!automatico) setOcupado(true);
    setAviso(null);
    if (onEstadoGuardado) onEstadoGuardado('inicio');
    try {
      const { data } = await api[metodo](`/presupuestos/${presupuestoId}/material-evento${ruta}`, {
        ...(jornadaId ? { jornada_id: jornadaId } : {}),
        ...cuerpo,
      });
      if (data && Array.isArray(data.filas)) {
        // En automático no se pierden las demás filas a medio escribir (ni
        // esta, si se ha seguido escribiendo)
        if (automatico) aplicar(data, (k, v) => k !== clave || v !== texto);
        else aplicar(data);
      }
      if (textoOk) setAviso({ tipo: 'ok', texto: textoOk });
      if (onEstadoGuardado) onEstadoGuardado('ok');
      return data;
    } catch (err) {
      setAviso({ tipo: 'error', texto: `No se pudo guardar: ${err?.response?.data?.detail || err.message}` });
      if (onEstadoGuardado) onEstadoGuardado('error');
      if (automatico) throw err;
      return undefined;
    } finally {
      if (!automatico) setOcupado(false);
    }
  };
  // Con autoguardado, todo va por la cola de la ficha (nunca dos a la vez)
  const enviar = (metodo, ruta, cuerpo, textoOk) => {
    const a = autoguardadoRef.current;
    if (!a) return enviarAhora(metodo, ruta, cuerpo, textoOk);
    return a.programar(`material:${presupuestoId}:${metodo}${ruta}:${JSON.stringify(cuerpo)}`,
      () => enviarAhora(metodo, ruta, cuerpo, textoOk), { inmediato: true }).catch(() => {});
  };

  // Autoguardado de una fila: lee el borrador y la fila actuales al guardarse
  const guardarFilaAuto = async (clave) => {
    const texto = borradoresRef.current[clave];
    const actual = datosRef.current;
    const fila = actual && !actual.validado ? (actual.filas || []).find(f => f.clave === clave) : null;
    if (texto === undefined || !fila) return undefined;
    const n = parseFloat(String(texto).replace(',', '.'));
    if (String(texto).trim() === '' || Number.isNaN(n) || n < 0) return undefined;
    if (Math.abs(n - (Number(fila.cantidad) || 0)) < 0.0001) {
      setBorradores(prev => { if (prev[clave] !== texto) return prev; const r = { ...prev }; delete r[clave]; return r; });
      return undefined;
    }
    const valor = Math.abs(n - (Number(fila.cantidad_calculada) || 0)) < 0.0001 ? null : n;
    return enviarAhora('put', '', { overrides: { [clave]: valor } }, null, { automatico: true, clave, texto });
  };
  const cambiarBorrador = (clave, texto) => {
    setBorradores(prev => ({ ...prev, [clave]: texto }));
    const a = autoguardadoRef.current;
    if (a) a.programar(`material_fila:${presupuestoId}:${clave}`, () => guardarFilaAuto(clave)).catch(() => {});
  };

  const guardarFila = (fila) => {
    const texto = borradores[fila.clave];
    if (texto === undefined) return;
    const n = parseFloat(String(texto).replace(',', '.'));
    if (String(texto).trim() === '' || Number.isNaN(n) || n < 0) {
      setBorradores(prev => { const r = { ...prev }; delete r[fila.clave]; return r; });
      setAviso({ tipo: 'error', texto: 'Escribe un número de unidades (0 o más).' });
      return;
    }
    if (Math.abs(n - (Number(fila.cantidad) || 0)) < 0.0001) {
      setBorradores(prev => { const r = { ...prev }; delete r[fila.clave]; return r; });
      return;
    }
    // Volver a lo calculado a mano equivale a restablecer
    const valor = Math.abs(n - (Number(fila.cantidad_calculada) || 0)) < 0.0001 ? null : n;
    enviar('put', '', { overrides: { [fila.clave]: valor } });
  };

  if (cargando && !datos) return <p className="text-sm text-[#5B6B64]">Cargando el material del evento...</p>;
  if (!datos) return <p className="text-sm text-[#8A1C12]" data-testid="material-error">{error}</p>;

  const validado = datos.validado || null;
  const filas = validado ? (validado.filas || []) : datos.filas;
  const editadas = filas.filter(f => f.editado).length;
  const desactualizada = !!(validado && datos.desactualizada);
  const cambiado = !!(validado && (datos.huella_cambiada || desactualizada));
  const cambiosValidacion = Array.isArray(datos.cambios_validacion) ? datos.cambios_validacion : [];
  const menajeCocina = Array.isArray(datos.menaje_cocina) ? datos.menaje_cocina : [];
  // Lo que llega al evento: líneas del proveedor y material de menús "Es alquiler"
  const numAlquiler = (Number(articulosAlquiler) || 0)
    + (Array.isArray(datos.materiales_alquiler_menus) ? datos.materiales_alquiler_menus.length : 0);
  let tonoCabecera = 'bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]';
  if (validado && !cambiado) tonoCabecera = 'bg-[#E3F3EC] border-[#BFE3D2] text-[#0F5E43]';

  return (
    <div className="flex flex-col gap-4" data-testid="pestana-material">
      {datos.multievento && (datos.jornadas || []).length > 0 && (
        <SelectorJornadas
          jornadas={datos.jornadas}
          jornadaId={datos.jornada_id}
          onChange={setJornadaId}
          idDe={(j) => j.jornada_id}
          testid="material-jornadas"
          prefijoTestid="material-jornada"
          etiqueta={(j) => (
            <>
              {j.etiqueta}
              <span className={`ml-2 px-1.5 py-px rounded-full text-xs font-bold ${j.huella_cambiada ? 'bg-[#FDF0DC] text-[#92400E]' : j.validado ? 'bg-[#E3F3EC] text-[#0F5E43]' : 'bg-[#EEF1EF] text-[#4A5A53]'}`}>
                {j.huella_cambiada ? 'revisar' : j.validado ? 'validado' : 'sin validar'}
              </span>
            </>
          )}
        />
      )}

      {/* Validación (obligatoria): estado grande y el botón, arriba del todo */}
      <div
        className={`flex flex-wrap items-center justify-between gap-3 rounded-[12px] border px-4 py-3.5 ${tonoCabecera}`}
        data-testid="material-cabecera-validacion"
        data-estado={validado ? (cambiado ? 'cambiado' : 'validado') : 'pendiente'}
      >
        <div className="min-w-0 flex flex-col gap-0.5">
          {!validado && (
            <>
              <span className="text-base font-extrabold">Pendiente de validar{datos.multievento && datos.etiqueta ? ` · ${datos.etiqueta}` : ''}</span>
              <span className="text-xs">Revisa la lista y las unidades y valídalas. Sin validar, la hoja de material sale como borrador.</span>
            </>
          )}
          {validado && !cambiado && (
            <span data-testid="material-validado" className="flex flex-col gap-0.5">
              <span className="text-base font-extrabold">
                Validado por {validado.por || 'alguien'}{validado.en ? ` el ${formatFechaHora(validado.en)}` : ''}
              </span>
              <span className="text-xs">Material validado: la hoja de material sale con esta lista y estas unidades.</span>
            </span>
          )}
          {cambiado && (
            <span data-testid="material-huella-cambiada" className="flex flex-col gap-0.5">
              <span className="text-base font-extrabold">Ha cambiado el presupuesto desde la validación: revisar</span>
              <span className="text-xs">
                Cambiaron personas, menús o servicios. La hoja sale como borrador con lo validado{validado.por ? ` por ${validado.por}` : ''}: reábrelo, revisa la lista y vuelve a validarla.
              </span>
            </span>
          )}
        </div>
        {validado ? (
          <button type="button" onClick={() => enviar('post', '/reabrir', {}, 'Material reabierto.')} disabled={ocupado} className={botonSecundario} data-testid="material-reabrir">
            Reabrir
          </button>
        ) : (
          <button type="button" onClick={() => enviar('post', '/validar', {}, 'Material validado.')} disabled={ocupado} className={botonPrincipal} data-testid="material-validar">
            Validar material
          </button>
        )}
      </div>
      {desactualizada && (
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-[10px] border px-3.5 py-3 text-sm bg-[#FDF0DC] border-[#E0A860] text-[#7A3A06]" data-testid="material-desactualizada">
          <div className="min-w-0 flex flex-col gap-1">
            <span className="font-extrabold">
              Revalidar: las cantidades han cambiado desde la validación{datos.fecha_validacion ? ` (${datos.fecha_validacion})` : ''}
            </span>
            <span className="text-xs">La hoja de material sale con las cantidades actuales y este aviso hasta que se revalide.</span>
            {cambiosValidacion.length > 0 && (
              <ul className="text-xs list-disc pl-4" data-testid="material-cambios-validacion">
                {cambiosValidacion.map(c => (
                  <li key={c.nombre}>{c.nombre}: {formatCantidad(c.validado)} validadas, {formatCantidad(c.actual)} ahora</li>
                ))}
              </ul>
            )}
          </div>
          <button type="button" onClick={() => enviar('post', '/validar', {}, 'Material revalidado.')} disabled={ocupado} className={botonPrincipal} data-testid="material-revalidar">
            Revalidar
          </button>
        </div>
      )}
      {aviso && (
        <p className={`text-xs ${aviso.tipo === 'ok' ? 'text-[#0F5E43]' : 'text-[#8A1C12]'}`} data-testid="material-mensaje">{aviso.texto}</p>
      )}

      <p className="text-xs text-[#5B6B64]">
        Todo va al mismo sitio: el material reutilizable entre servicios no se suma (se toma el máximo) y el consumible o desechable sí.
        {datos.multievento ? ' En multievento, cada jornada tiene su lista, su validación y su hoja de material.' : ''}
      </p>
      {!validado && datos.ajustes_desfasados && (
        <div className="rounded-[10px] border px-3.5 py-3 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="material-ajustes-desfasados">
          Hay unidades ajustadas a mano con otro presupuesto (personas, menús o servicios distintos): revísalas antes de validar.
        </div>
      )}

      {filas.length === 0 ? (
        <p className="text-sm text-[#5B6B64]" data-testid="material-vacio">
          {validado ? 'Se validó sin material.' : 'Los menús de este presupuesto no llevan material.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="material-tabla">
          {filas.map((f) => {
            const borrador = borradores[f.clave];
            const ayuda = [
              textoRegla(f),
              ...((f.detalle || []).length > 1 ? [f.detalle.map(d => `${d.menu}: ${formatCantidad(d.cantidad)}`).join(' · ')] : []),
            ].join(' · ');
            return (
              <div
                key={f.clave}
                className="border border-[#E1E6E3] rounded-lg px-3 py-2 flex items-start justify-between gap-2 bg-white"
                title={ayuda}
                data-testid={`material-fila-${f.clave}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#16221D]">
                    {f.nombre}
                    {f.es_caja && <span className="ml-1.5 px-1.5 py-px rounded text-xs md:text-[11px] font-bold text-white bg-[#C2870A]">CAJA</span>}
                  </div>
                  {(f.composicion || []).length > 0 && (
                    <div className="text-xs text-[#5B6B64]">
                      {f.composicion.map(c => `${formatCantidad(c.uds_total)} ${c.nombre}`).join(' · ')}
                    </div>
                  )}
                  {ayuda && (
                    // Móvil: la regla y el reparto por menú (en escritorio, en el title)
                    <div className="md:hidden text-xs text-[#5B6B64]" data-testid={`material-ayuda-movil-${f.clave}`}>{ayuda}</div>
                  )}
                  {f.editado && (
                    <div className="text-xs md:text-[11px] text-[#5B6B64] tabular-nums">Calc. {conUnidad(f.cantidad_calculada, f.unidad)}</div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  {validado ? (
                    <span className="font-bold tabular-nums text-[#16221D]">
                      {conUnidad(f.cantidad, f.unidad)}{f.editado ? <span className="block text-xs md:text-[11px] font-normal text-[#92400E]">ajustado a mano</span> : null}
                    </span>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      {f.editado && (
                        <button
                          type="button"
                          onClick={() => enviar('put', '', { overrides: { [f.clave]: null } })}
                          disabled={ocupado}
                          className="min-h-[40px] md:min-h-0 text-[13px] md:text-xs font-semibold text-[#11704F] hover:underline disabled:opacity-50"
                          title="Volver a lo calculado"
                          data-testid={`material-restablecer-${f.clave}`}
                        >
                          Restablecer
                        </button>
                      )}
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={borrador !== undefined ? borrador : f.cantidad}
                        onChange={(e) => cambiarBorrador(f.clave, e.target.value)}
                        onBlur={() => {
                          // Con autoguardado lo guarda la cola (al salir del campo se fuerza);
                          // aquí solo se avisa si lo escrito no es un número válido
                          if (!autoguardadoRef.current) {
                            guardarFila(f);
                            return;
                          }
                          const t = borradoresRef.current[f.clave];
                          const n = parseFloat(String(t ?? '').replace(',', '.'));
                          if (t !== undefined && (String(t).trim() === '' || Number.isNaN(n) || n < 0)) {
                            setBorradores(prev => { const r = { ...prev }; delete r[f.clave]; return r; });
                            setAviso({ tipo: 'error', texto: 'Escribe un número de unidades (0 o más).' });
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        disabled={ocupado && !autoguardado}
                        className={`w-20 md:w-16 h-11 md:h-auto px-2 py-1.5 text-base md:text-sm text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary tabular-nums ${f.editado ? 'border-[#E0A860] bg-[#FFFBF2]' : 'border-[#CBD3CF]'}`}
                        aria-label={`Unidades de ${f.nombre}`}
                        data-testid={`material-uds-${f.clave}`}
                      />
                      {f.unidad && f.unidad !== 'ud' && <span className="text-xs text-[#5B6B64]">{f.unidad}</span>}
                    </div>
                  )}
                  {f.envase && (f.envase.entero && f.envase.carga ? (
                    // Va entero (racks): lo que se carga, más visible que lo que se usa
                    <div className="text-xs font-semibold text-[#16221D] tabular-nums" data-testid={`material-envase-${f.clave}`}>
                      Se llevan {f.envase.carga}
                    </div>
                  ) : (
                    <div className="text-xs md:text-[11px] text-[#5B6B64] tabular-nums" data-testid={`material-envase-${f.clave}`}>{f.envase.texto}</div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {menajeCocina.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="material-menaje-cocina">
          <div>
            <h4 className="text-xs font-bold tracking-wider uppercase text-[#5B6B64]">Menaje de cocina</h4>
            <p className="text-xs text-[#5B6B64]">
              Cafeteras, termos, etiquetas, cuencos, pinzas y bandejas de alérgicos: se calculan como en la orden de cocina y salen en la hoja de material. No se editan aquí.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full md:min-w-[420px] border-collapse">
              <thead>
                <tr>
                  <th className={`${claseTh} text-left`}>Material</th>
                  <th className={`${claseTh} text-right`}>Unidades</th>
                </tr>
              </thead>
              <tbody>
                {menajeCocina.map(m => (
                  <tr key={`${m.origen || 'cocina'}-${m.nombre}`} data-testid={`material-menaje-${m.nombre}`}>
                    <td className={claseTd}>
                      <div className="font-semibold text-[#16221D]">{m.nombre}</div>
                      {m.nota && <div className="text-xs text-[#5B6B64]">{m.nota}</div>}
                    </td>
                    <td className={`${claseTd} text-right tabular-nums font-bold text-[#16221D]`}>
                      {conUnidad(m.cantidad, m.unidad)}
                      {m.envase && (
                        <div className="text-xs md:text-[11px] font-normal text-[#5B6B64] tabular-nums" data-testid={`material-menaje-envase-${m.nombre}`}>{m.envase.texto}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {onAbrirAlquiler && numAlquiler > 0 && (
        <p className="text-xs text-[#5B6B64]" data-testid="material-alquiler-llega">
          Material de alquiler: llega al evento ({numAlquiler} {numAlquiler === 1 ? 'artículo' : 'artículos'}).{' '}
          <button type="button" onClick={onAbrirAlquiler} className="font-semibold text-duetto-primary hover:underline" data-testid="material-alquiler-ver">
            Ver
          </button>
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-[#F7F9F8] px-3.5 py-3 text-sm">
        <span className="text-[#5B6B64]">
          {filas.length} {filas.length === 1 ? 'material' : 'materiales'}
          {editadas > 0 ? ` · ${editadas} ${editadas === 1 ? 'ajustado' : 'ajustados'} a mano` : ''}
          {!validado && datos.actualizado_por ? ` · último ajuste de ${datos.actualizado_por}` : ''}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {onDescargarHoja && (
            <>
              <label className="flex items-center gap-1.5 text-xs text-[#5B6B64]">
                Diseño
                <select
                  value={diseno}
                  onChange={(e) => setDiseno(e.target.value)}
                  className="h-11 md:h-[34px] px-2 text-base md:text-xs border border-[#CBD3CF] rounded-lg bg-white text-[#16221D] focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  data-testid="material-diseno-hoja"
                >
                  {DISENOS_HOJA.map(d => <option key={d.valor || 'actual'} value={d.valor}>{d.texto}</option>)}
                </select>
              </label>
              <button type="button" onClick={() => onDescargarHoja(datos.jornada_id || null, diseno || null)} className={botonSecundario} data-testid="material-descargar-hoja">
                {validado && !cambiado ? 'Descargar hoja de material' : 'Descargar hoja (borrador)'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PestanaMaterial;
